import React, { forwardRef } from 'react';
import { Slide, SlideProps } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

export type SlideDirection = 'up' | 'down' | 'left' | 'right';

export interface SlideTransitionProps extends Omit<TransitionProps, 'children'> {
  /**
   * Direction of the slide animation
   * @default 'up'
   */
  direction?: SlideDirection;
  /**
   * Children to render
   */
  children: React.ReactElement;
}

/**
 * Reusable slide transition component for dialogs and panels.
 * Commonly used for mobile full-screen dialogs that slide up from the bottom.
 *
 * @example
 * // In a Dialog component
 * <Dialog
 *   TransitionComponent={SlideUpTransition}
 *   fullScreen={isMobile}
 * >
 *   ...
 * </Dialog>
 *
 * @example
 * // Custom direction
 * const SlideLeftTransition = forwardRef((props, ref) => (
 *   <SlideTransition direction="left" ref={ref} {...props} />
 * ));
 */
export const SlideTransition = forwardRef<unknown, SlideTransitionProps>(
  function SlideTransition({ direction = 'up', children, ...props }, ref) {
    return (
      <Slide direction={direction} ref={ref} {...props}>
        {children}
      </Slide>
    );
  }
);

/**
 * Pre-configured slide up transition (most common for mobile dialogs)
 */
export const SlideUpTransition = forwardRef<unknown, Omit<SlideTransitionProps, 'direction'>>(
  function SlideUpTransition(props, ref) {
    return <SlideTransition direction="up" ref={ref} {...props} />;
  }
);

/**
 * Pre-configured slide down transition
 */
export const SlideDownTransition = forwardRef<unknown, Omit<SlideTransitionProps, 'direction'>>(
  function SlideDownTransition(props, ref) {
    return <SlideTransition direction="down" ref={ref} {...props} />;
  }
);

/**
 * Pre-configured slide left transition (for side panels)
 */
export const SlideLeftTransition = forwardRef<unknown, Omit<SlideTransitionProps, 'direction'>>(
  function SlideLeftTransition(props, ref) {
    return <SlideTransition direction="left" ref={ref} {...props} />;
  }
);

/**
 * Pre-configured slide right transition (for side panels)
 */
export const SlideRightTransition = forwardRef<unknown, Omit<SlideTransitionProps, 'direction'>>(
  function SlideRightTransition(props, ref) {
    return <SlideTransition direction="right" ref={ref} {...props} />;
  }
);

export default SlideTransition;
